import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';

// Styled Components
const MapContainer = styled.div`
  width: 100%;
  margin-top: 10px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const MapToggleButton = styled.button`
  padding: 8px 16px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 8px;
  transition: background 0.3s;

  &:hover:not(:disabled) {
    background: #0056b3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const MapActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const SecondaryMapButton = styled(MapToggleButton)`
  background: #fff;
  color: #007bff;
  border: 1px solid #007bff;
  margin-top: 0;

  &:hover:not(:disabled) {
    background: #eef6ff;
  }
`;

const AddressDisplay = styled.div`
  margin-top: 8px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 14px;
  color: #495057;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
`;

const LoadingContainer = styled.div`
  margin-top: 8px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
`;

const TimeoutWarning = styled.div`
  margin-top: 8px;
  padding: 8px;
  background: #fff3cd;
  border-radius: 4px;
  font-size: 11px;
`;

const ErrorCode = styled.code`
  font-size: 12px;
  background: #f8f9fa;
  padding: 4px;
  border-radius: 4px;
  display: block;
  margin-top: 8px;
`;

const SolutionSteps = styled.ol`
  margin-top: 8px;
  padding-left: 20px;
  
  li {
    margin-bottom: 4px;
  }
`;

const InfoBox = styled.div`
  margin-top: 12px;
  padding: 8px;
  background: ${props => props.type === 'warning' ? '#fff3cd' : '#d1ecf1'};
  border-radius: 4px;
  font-size: 12px;
`;

// Constants
const MAP_CONTAINER_STYLE = {
  width: '100%',
  height: '400px'
};

const DEFAULT_CENTER = {
  lat: 30.0444, // downtown Cairo, Egypt — default for all users
  lng: 31.2357,
};

const DEFAULT_ZOOM = 12;

const LOADING_TIMEOUT_MS = 10000; // 10 seconds
const COORDINATE_VALUE_REGEX = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/;


// Google Maps libraries - must be a constant to prevent reloading
const GOOGLE_MAPS_LIBRARIES = ['places'];

// Helper Functions
const getSolutionSteps = (errorMessage, hostname) => {
  const isAuthError = errorMessage.includes('API key') || 
                     errorMessage.includes('not authorized') || 
                     errorMessage.includes('referer');
  const isBillingError = errorMessage.includes('billing') || 
                        errorMessage.includes('quota');
  const isApiDisabledError = errorMessage.includes('not been used') || 
                            errorMessage.includes('not enabled');

  if (isAuthError) {
    return [
      '1. Go to Google Cloud Console → APIs & Services → Credentials',
      '2. Click your Google Maps API key in the Credentials panel',
      '3. Under "Application restrictions" → Select "HTTP referrers (web sites)"',
      `4. Add: https://${hostname}/*`,
      '5. Add: https://flea-marketplace.com/*',
      '6. Add: https://www.flea-marketplace.com/*',
      '7. Save and wait 5 minutes for changes to propagate'
    ];
  }
  
  if (isBillingError) {
    return [
      '1. Go to Google Cloud Console → Billing (this is required — Maps shows “For development purposes only” until billing is on)',
      '2. Link a billing account to the project that owns this API key',
      '3. Google still provides a monthly free Maps credit; a card is required to activate',
      '4. Enable Maps JavaScript API + Geocoding API under APIs & Services → Library',
      '5. Wait a few minutes, then hard-refresh the site'
    ];
  }
  
  if (isApiDisabledError) {
    return [
      '1. Go to Google Cloud Console → APIs & Services → Library',
      '2. Search for "Maps JavaScript API"',
      '3. Click "Enable"',
      '4. Search for "Geocoding API"',
      '5. Click "Enable"',
      '6. Wait a few minutes for APIs to activate'
    ];
  }
  
  return [
    '1. Check browser console (F12) for detailed error',
    '2. Verify API key is set in Cloudflare Pages',
    '3. Ensure you redeployed after adding env var',
    '4. Check Google Cloud Console for API status'
  ];
};

const ErrorDisplay = ({ errorMessage, mapsApiKey, hostname }) => {
  const solutionSteps = getSolutionSteps(errorMessage, hostname);
  
  return (
    <ErrorMessage>
      <strong>❌ Error loading Google Maps:</strong>
      <br />
      <ErrorCode>{errorMessage}</ErrorCode>
      <div style={{ marginTop: '12px', fontSize: '12px' }}>
        <strong>Solution Steps:</strong>
        <SolutionSteps>
          {solutionSteps.map((step, idx) => (
            <li key={idx}>{step}</li>
          ))}
        </SolutionSteps>
        <InfoBox type="warning">
          <strong>Quick Check:</strong>
          <br />
          • API Key in build: {mapsApiKey ? '✅ YES' : '❌ NO'}
          <br />
          • Current domain: {hostname}
          <br />
          • Check console (F12) for more details
        </InfoBox>
      </div>
    </ErrorMessage>
  );
};

const ApiKeyMissingDisplay = () => {
  const isProduction = process.env.NODE_ENV === 'production';
  
  return (
    <ErrorMessage>
      <strong>❌ Google Maps API key not configured</strong>
      <div style={{ marginTop: '12px', fontSize: '12px' }}>
        {isProduction ? (
          <>
            <strong>This means the env var wasn't available during build!</strong>
            <br />
            <SolutionSteps>
              <li>Go to Cloudflare Pages → Your Project → Settings → Environment Variables</li>
              <li>
                Add: <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> ={' '}
                <code>your-google-maps-api-key</code>
              </li>
              <li>
                <strong>IMPORTANT:</strong> Make sure it's set for "Production" environment
              </li>
              <li>Click "Save"</li>
              <li>
                <strong>Redeploy</strong> your site (trigger a new deployment)
              </li>
              <li>Wait for build to complete</li>
            </SolutionSteps>
            <InfoBox type="info">
              <strong>⚠️ Note:</strong> React environment variables are baked into the build at BUILD TIME.
              You MUST redeploy after adding the variable!
            </InfoBox>
          </>
        ) : (
          <>
            Add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to your <code>.env.local</code> file:
            <br />
            <ErrorCode>
              REACT_APP_GOOGLE_MAPS_API_KEY=your-google-maps-api-key
            </ErrorCode>
          </>
        )}
      </div>
    </ErrorMessage>
  );
};

const LoadingDisplay = ({ loadingTimeout }) => (
  <LoadingContainer>
    <small>⏳ Loading Google Maps...</small>
    {loadingTimeout && (
      <TimeoutWarning>
        <strong>⚠️ Taking longer than expected...</strong>
        <br />
        This might indicate:
        <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
          <li>API key restrictions blocking this domain</li>
          <li>Maps JavaScript API not enabled</li>
          <li>Network issues</li>
        </ul>
        Check browser console (F12) for errors.
      </TimeoutWarning>
    )}
  </LoadingContainer>
);

/** User-facing geolocation error text, translated via i18next. */
const useGeoErrorMessages = () => {
  const { t } = useTranslation();
  return {
    unsupported: t('listing.geoUnsupported'),
    denied: t('listing.geoDenied'),
    prefix: t('listing.geoFailedPrefix'),
    deniedSuffix: t('listing.geoDeniedSuffix'),
    unavailable: t('listing.geoUnavailable'),
    timeout: t('listing.geoTimeout'),
    unknown: t('listing.geoUnknown'),
  };
};

// Main Component
const LocationPicker = ({ value, onChange, apiKey }) => {
  const { t } = useTranslation();
  const geoErrors = useGeoErrorMessages();
  const mapLocationFallback = t('listing.mapLocationFallback');
  // State
  const [showMap, setShowMap] = useState(false);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [address, setAddress] = useState(value || '');
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const mapRef = useRef(null);

  // Get API key from props or environment variable
  const mapsApiKey = apiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  // Debug logging
  useEffect(() => {
    const debugInfo = {
      hasApiKey: !!mapsApiKey,
      apiKeyPrefix: mapsApiKey ? `${mapsApiKey.substring(0, 15)}...` : 'NOT SET',
      apiKeyLength: mapsApiKey ? mapsApiKey.length : 0,
      environment: process.env.NODE_ENV,
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      allEnvVars: Object.keys(process.env)
        .filter(k => k.startsWith('REACT_APP'))
        .join(', ') || 'NONE',
      keyConfigured: !!mapsApiKey
    };
    
    logger.log('🗺️ Google Maps Debug:', debugInfo);
    
    if (process.env.NODE_ENV === 'production' && !mapsApiKey) {
      logger.error('❌ CRITICAL: Google Maps API key is NOT SET in production build!');
      logger.error('This means the environment variable was not available during build time.');
      logger.error('Solution: Add REACT_APP_GOOGLE_MAPS_API_KEY to Cloudflare Pages and REDEPLOY');
    }
  }, [mapsApiKey]);

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: mapsApiKey || undefined, // Pass undefined if empty to avoid "NoApiKeys" warning
    libraries: GOOGLE_MAPS_LIBRARIES, // Use constant to prevent reloading
    version: 'weekly'
  });

  // Loading timeout warning
  useEffect(() => {
    if (!isLoaded && mapsApiKey) {
      const timeout = setTimeout(() => {
        setLoadingTimeout(true);
        logger.warn('⚠️ Google Maps taking longer than expected to load. Check API key restrictions.');
      }, LOADING_TIMEOUT_MS);
      
      return () => clearTimeout(timeout);
    } else {
      setLoadingTimeout(false);
    }
  }, [isLoaded, mapsApiKey]);

  // Callbacks
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    logger.log('✅ Google Map loaded successfully');
    // Keep camera on the intended center (Cairo by default) after tiles load.
    if (map && center?.lat != null && center?.lng != null) {
      map.panTo(center);
      map.setZoom(DEFAULT_ZOOM);
    }
  }, [center]);

  useEffect(() => {
    const rawValue = typeof value === 'string' ? value.trim() : '';
    if (!rawValue) {
      setAddress('');
      return;
    }

    const match = rawValue.match(COORDINATE_VALUE_REGEX);
    if (!match) {
      setAddress(rawValue);
      return;
    }

    const lat = Number(match[1]);
    const lng = Number(match[2]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setAddress(mapLocationFallback);
      return;
    }

    const pos = { lat, lng };
    setCenter(pos);
    setMarkerPosition(pos);

    if (!window.google?.maps?.Geocoder) {
      setAddress(mapLocationFallback);
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results?.[0]?.formatted_address) {
        setAddress(results[0].formatted_address);
      } else {
        setAddress(mapLocationFallback);
      }
    });
  }, [value, mapLocationFallback]);

  const getAddressFromLatLng = useCallback((position) => {
    if (!window.google?.maps?.Geocoder) {
      logger.warn('Google Maps Geocoder not available');
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    const lat = typeof position.lat === 'function' ? position.lat() : position.lat;
    const lng = typeof position.lng === 'function' ? position.lng() : position.lng;

    geocoder.geocode({ location: position }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const formattedAddress = results[0].formatted_address;
        setAddress(formattedAddress);
        onChange(formattedAddress, { latitude: lat, longitude: lng });
      } else {
        logger.warn('Geocoding failed:', status);
        setAddress(mapLocationFallback);
        onChange(mapLocationFallback, { latitude: lat, longitude: lng });
      }
    });
  }, [onChange, mapLocationFallback]);

  const applyGpsPosition = useCallback((pos) => {
    setCenter(pos);
    setMarkerPosition(pos);
    setShowMap(true);
    if (mapRef.current) {
      mapRef.current.panTo(pos);
      mapRef.current.setZoom(DEFAULT_ZOOM);
    }
    if (window.google?.maps?.Geocoder) {
      getAddressFromLatLng(pos);
      return;
    }
    // Maps JS failed or still loading — still save usable coordinates.
    const fallbackAddress = `${pos.lat.toFixed(6)}, ${pos.lng.toFixed(6)}`;
    setAddress(fallbackAddress);
    onChange(fallbackAddress, { latitude: pos.lat, longitude: pos.lng });
  }, [getAddressFromLatLng, onChange]);

  const getCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      logger.warn('Geolocation is not supported by this browser');
      setLocationError(geoErrors.unsupported);
      return;
    }

    setLocationError(null);

    try {
      if (navigator.permissions && navigator.permissions.query) {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
        if (permissionStatus.state === 'denied') {
          setLocationError(geoErrors.denied);
          return;
        }
      }
    } catch (permError) {
      logger.debug('Permissions API not available:', permError);
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        applyGpsPosition({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocationError(null);
      },
      (error) => {
        logger.error('Error getting location:', error);
        let errorMessage = geoErrors.prefix;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += geoErrors.deniedSuffix;
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += geoErrors.unavailable;
            break;
          case error.TIMEOUT:
            errorMessage += geoErrors.timeout;
            break;
          default:
            errorMessage += geoErrors.unknown;
            break;
        }
        setLocationError(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  }, [applyGpsPosition, geoErrors]);

  const handleMapClick = useCallback((event) => {
    if (!event?.latLng) return;
    
    const clickedPos = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
    setMarkerPosition(clickedPos);
    getAddressFromLatLng(clickedPos);
  }, [getAddressFromLatLng]);

  const toggleMap = useCallback(() => {
    setShowMap((prev) => {
      const opening = !prev;
      if (opening) {
        const rawValue = typeof value === 'string' ? value.trim() : '';
        const match = rawValue.match(COORDINATE_VALUE_REGEX);
        if (match) {
          const lat = Number(match[1]);
          const lng = Number(match[2]);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            const pos = { lat, lng };
            setCenter(pos);
            setMarkerPosition(pos);
            return true;
          }
        }
        // Production default: open on Cairo — do not auto-jump to GPS.
        if (!markerPosition) {
          setCenter(DEFAULT_CENTER);
        }
      }
      return opening;
    });
  }, [value, markerPosition]);

  const mapsBlocked = Boolean(loadError) || !mapsApiKey;
  const mapsReady = Boolean(isLoaded && mapsApiKey && !loadError);

  // Main render — always keep GPS fallback even when Maps JS fails
  // (billing disabled / invalid key / referrer restrictions).
  return (
    <div>
      {loadError ? (
        <ErrorDisplay
          errorMessage={loadError.message || 'Unknown error'}
          mapsApiKey={mapsApiKey}
          hostname={window.location.hostname}
        />
      ) : null}
      {!mapsApiKey ? <ApiKeyMissingDisplay /> : null}
      {mapsApiKey && !isLoaded && !loadError ? (
        <LoadingDisplay loadingTimeout={loadingTimeout} />
      ) : null}

      {locationError && (
        <ErrorMessage style={{ marginTop: '8px' }}>
          {locationError}
        </ErrorMessage>
      )}

      <MapActions>
        <MapToggleButton
          type="button"
          onClick={toggleMap}
          disabled={!mapsReady}
          aria-label={showMap ? t('listing.hideMap') : t('listing.showMapAria')}
        >
          {showMap ? `📍 ${t('listing.hideMap')}` : `📍 ${t('listing.pickLocationOnMap')}`}
        </MapToggleButton>
        <SecondaryMapButton
          type="button"
          onClick={getCurrentLocation}
          aria-label={t('listing.useCurrentLocation')}
        >
          📍 {t('listing.useCurrentLocation')}
        </SecondaryMapButton>
      </MapActions>

      {mapsBlocked ? (
        <InfoBox type="warning">
          {t('listing.mapsBlockedNotice')}
        </InfoBox>
      ) : null}

      {address && (
        <AddressDisplay>
          <strong>{t('listing.selectedLocation')}</strong> {address}
        </AddressDisplay>
      )}

      {showMap && mapsReady && (
        <MapContainer>
          <GoogleMap
            mapContainerStyle={MAP_CONTAINER_STYLE}
            center={center}
            zoom={DEFAULT_ZOOM}
            onLoad={onMapLoad}
            onClick={handleMapClick}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
              zoomControl: true,
              disableDefaultUI: false
            }}
          >
            {markerPosition && (
              <Marker
                position={markerPosition}
                draggable={true}
                onDragEnd={handleMapClick}
              />
            )}
          </GoogleMap>
        </MapContainer>
      )}
    </div>
  );
};

export default LocationPicker;
